import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Box, ListItemButton, ListItemText, Collapse, List, Typography } from "@mui/material";

import APP_ROUTES from "../../../../routers/routeConstants";
import {
  ChatSNIcon,
  DashboardIcon,
  ExploreIcon,
  FeedIcon,
  NetworkIcon,
  PaymentIcon,
  GoServiceIcon,
  GinoIcon,
  SettingsIcon,
  ExpandLessIcon,
  ExpandMoreIcon,
} from "../../../../assets/icons/MUI/LayoutIcons";
import AiAnimatedIcon from "./AiAnimatedIcon";

type MenuItemType = {
  label: string;
  to?: string;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children?: MenuItemType[];
  baseRoute?: string;
};

const menuList: MenuItemType[] = [
  {
    label: "layout.sideNav.gino",
    icon: <GinoIcon />,
    to: APP_ROUTES.GINO,
    endIcon: <AiAnimatedIcon />,
  },
  {
    label: "layout.sideNav.dashboard",
    icon: <DashboardIcon />,
    to: APP_ROUTES.DASHBOARD,
  },
  {
    label: "layout.sideNav.feed",
    icon: <FeedIcon />,
    to: APP_ROUTES.FEED,
  },
  {
    label: "layout.sideNav.goHire",
    icon: <GoServiceIcon />,
    baseRoute: APP_ROUTES.GO_HIRE,
    children: [
      {
        label: "layout.sideNav.jobs",
        to: APP_ROUTES.JOBS,
      },
      {
        label: "layout.sideNav.overviewServices",
        to: APP_ROUTES.OVERVIEW_SERVICES,
      },
      {
        label: "layout.sideNav.myRequests",
        to: APP_ROUTES.MY_SERVICES,
      },
    ],
  },
  {
    label: "layout.sideNav.explore",
    icon: <ExploreIcon />,
    baseRoute: APP_ROUTES.EXPLORE,
    children: [
      {
        label: "layout.sideNav.events",
        to: APP_ROUTES.EVENTS,
      },
      {
        label: "layout.sideNav.goStar",
        to: APP_ROUTES.GO_STAR,
      },
    ],
  },
  {
    label: "layout.sideNav.payment",
    icon: <PaymentIcon />,
    to: APP_ROUTES.PAYMENT,
  },

  {
    label: "layout.sideNav.network",
    icon: <NetworkIcon />,
    to: APP_ROUTES.NETWORK,
    // children: [
    //   {
    //     label: "layout.sideNav.followers",
    //     to: APP_ROUTES.NETWORK,
    //   },
    //   {
    //     label: "layout.sideNav.following",
    //     to: APP_ROUTES.NETWORK,
    //   },
    // ],
  },

  {
    label: "layout.sideNav.chat",
    icon: <ChatSNIcon />,
    to: APP_ROUTES.CHAT,
  },

  {
    label: "layout.sideNav.settings",
    icon: <SettingsIcon />,
    baseRoute: APP_ROUTES.SETTINGS,
    children: [
      {
        label: "layout.sideNav.plans",
        to: APP_ROUTES.PLANS_OVERVIEW,
      },
      {
        label: "layout.sideNav.team",
      },
      {
        label: "layout.sideNav.managedFootballEntities",
      },
    ],
  },
];

export const RoutingMenuList = () => {
  return (
    <Box component="ul" sx={styles.menuList}>
      {menuList.map(({ label, icon, endIcon, to, children, baseRoute }) => (
        <MenuItem
          key={label}
          label={label}
          icon={icon}
          endIcon={endIcon}
          to={to}
          children={children}
          baseRoute={baseRoute}
        />
      ))}
    </Box>
  );
};

const MenuItem = ({
  label,
  icon,
  endIcon,
  to,
  children,
  isChild,
  baseRoute,
}: {
  label: string;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  to?: string;
  children?: MenuItemType[];
  isChild?: boolean;
  baseRoute?: string;
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  let isActive = false;
  const targetPath = to?.split("/").at(-1)?.split("?")[0];

  const targetRootPath = to?.split("/").at(1)?.split("?")[0];
  const currentRootPath = location.pathname.split("/").at(1)?.split("?")[0];

  if (baseRoute) {
    isActive = baseRoute.split("/").at(1)?.split("?")[0] === currentRootPath;
  } else {
    if (isChild) {
      isActive = location.pathname.split("/").includes(targetPath!);
    } else {
      isActive = currentRootPath == targetRootPath;
    }
  }

  const [open, setOpen] = useState(isActive || false);
  const handleClick = () => setOpen((prev) => !prev);

  if (!children) {
    return (
      <Box component="li">
        <Typography
          component={to ? Link : "div"}
          {...(to ? { to } : {})}
          sx={{
            ...styles.menuItem(isActive, Boolean(isChild)),
            cursor: to ? "pointer" : "unset",
            pointerEvents: to ? "auto" : "none",
            opacity: to ? 1 : 0.5,
          }}
        >
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {icon}
            {t(label)}
          </Box>
          {endIcon && (
            <Box component="span" sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
              {endIcon}
            </Box>
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ListItemButton onClick={handleClick} sx={styles.menuItemButton(isActive)}>
        {icon}
        <ListItemText primary={t(label)} sx={{}} />
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit sx={styles.menuItemCollapse}>
        <List component="div" disablePadding>
          {children?.map(
            (item: MenuItemType) =>
              item && (
                <MenuItem
                  key={item.label}
                  label={item.label}
                  icon={item.icon}
                  endIcon={item.endIcon}
                  to={item.to}
                  isChild
                />
              )
          )}
        </List>
      </Collapse>
    </>
  );
};

const styles = {
  menuList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    m: 0,
  },
  menuItem: (isActive: boolean, isChild: boolean) => ({
    borderRadius: "6px",
    display: "flex",
    padding: "8px 12px",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    color: isActive ? "#111" : "var(--Dark---600, #707070)",
    backgroundColor: isActive ? "#F5F6F5" : "transparent",
    fontFamily: "Montserrat",
    fontSize: isChild ? "12px" : "14px",
    fontStyle: "normal",
    fontWeight: isActive ? 600 : 400,
    lineHeight: isChild ? "18px" : "20px",
    textDecoration: "none",
    "& > span:first-of-type svg path": {
      stroke: isActive ? "#008B07" : "var(--Dark---600, #707070)",
    },
    "&:hover": {
      color: "#111",
      backgroundColor: "#F5F6F5",
      fontWeight: 600,
      "& > span:first-of-type svg path": {
        stroke: "#008B07",
      },
    },
  }),
  menuItemButton: (isActive: boolean) => ({
    borderRadius: "6px",
    display: "flex",
    padding: "8px 12px",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    textDecoration: "none",
    backgroundColor: isActive ? "#F5F6F5" : "transparent",
    "& .MuiTypography-root": {
      color: isActive ? "#111" : "var(--Dark---600, #707070)",
      fontFamily: "Montserrat",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: isActive ? 600 : 400,
      lineHeight: "20px",
    },
    "& svg path": {
      stroke: isActive ? "#008B07" : "var(--Dark---600, #707070)",
    },
    "&:hover": {
      "& .MuiTypography-root": {
        color: "#111",
        backgroundColor: "#F5F6F5",
        fontWeight: 600,
      },
      "& svg path": {
        stroke: "#008B07",
      },
    },
  }),
  menuItemCollapse: {
    marginLeft: "30px",
    "& .MuiList-root": {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
  },
};
